import {useEffect, useState} from "react";
import ReactImageUploading from "react-images-uploading";
import {ImageListType} from "react-images-uploading/dist/typings";
import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import {apiBindings} from "../../utils/constants";
import {post, postFile} from "../../utils/http.client";
import {prepareImageUpload} from "../../utils/file.utils";
import {ColorControl} from "../../elements/colorInputLabel.element";
import {RiftColor} from "../../models/remoteConstants.model";
import {
    AVATAR_ELEMENT_TYPES, AVATAR_TYPE_LABELS, OPTIONAL_AVATAR_TYPES,
    AvatarCatalogue, AvatarElement, AvatarElementType, normalizeCatalogue,
} from "../../models/avatars.model";
import TintedPreview from "./tintedPreview.element";
import AvatarComposite from "./avatarComposite.element";

// The Avatar tab: the global avatar element catalogue. Unlike everything on
// the branch tabs, this is NOT branch data — it lives in its own avatars.json
// blob, every upload/delete saves immediately to the dev catalogue (no export
// step, no undo), and "Publish to Live" pushes the whole catalogue (plus its
// element images) to the live service. Tester+ for the tab and dev-catalogue
// edits, Admin+ for Publish to Live — matching the backend gates in SetAvatars.

// Read a just-selected image file's natural pixel dimensions locally (no
// network). Duplicated from app.context's module-private readImageDimensions.
// Returns undefined if the file can't be decoded as an image.
async function readImageDimensions(file: File): Promise<{width: number; height: number} | undefined> {
    try {
        const bitmap = await createImageBitmap(file);
        const dimensions = {width: bitmap.width, height: bitmap.height};
        bitmap.close();
        return dimensions;
    } catch {
        return undefined;
    }
}

// Preview tints are opaque — the element art's alpha is the shape, so the
// tint's own alpha is meaningless here.
const riftToCss = (c: RiftColor) =>
    `rgb(${Math.round(c.r * 255)}, ${Math.round(c.g * 255)}, ${Math.round(c.b * 255)})`;

// Palette entries use the avatar spec's color format: "RRGGBB", no '#', no alpha.
const riftToHex = (c: RiftColor) =>
    [c.r, c.g, c.b].map(v => Math.round(v * 255).toString(16).padStart(2, '0')).join('').toUpperCase();

const hexToRift = (hex: string): RiftColor => ({
    r: parseInt(hex.substring(0, 2), 16) / 255,
    g: parseInt(hex.substring(2, 4), 16) / 255,
    b: parseInt(hex.substring(4, 6), 16) / 255,
    a: 1,
});

const MID_GREY: RiftColor = {r: 0.5, g: 0.5, b: 0.5, a: 1};

function defaultTints(): Record<AvatarElementType, RiftColor> {
    return {face: MID_GREY, eyes: MID_GREY, facialHair: MID_GREY, hair: MID_GREY, hat: MID_GREY};
}

function AvatarEditorView() {
    const {setNotification, setLoading, confirm, confirmDelete} = useApp();
    const {canEditAvatars, canPublishAvatarsLive} = useAccess();

    // View-local by design: the catalogue is not part of the branch state, so
    // it deliberately bypasses the integrator's command/undo history.
    const [catalogue, setCatalogue] = useState<AvatarCatalogue | undefined>(undefined);
    const [fetchFailed, setFetchFailed] = useState<boolean>(false);
    // Per-section preview tint (display only, never saved).
    const [tints, setTints] = useState<Record<AvatarElementType, RiftColor>>(defaultTints);

    function fetchCatalogue() {
        setFetchFailed(false);
        fetch(`${apiBindings.api}getAvatars`, {cache: 'no-store'})
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then((raw: string | AvatarCatalogue) => {
                // The backend wraps the catalogue string in an OkObjectResult
                // (matching getAttributeManifest), so the parsed body may be a
                // JSON-stringified catalogue. Decode once more if so.
                const c: AvatarCatalogue = typeof raw === 'string' ? JSON.parse(raw) : raw;
                // Pre-palette catalogues lack the palettes key.
                setCatalogue(normalizeCatalogue(c));
            })
            // NO empty-catalogue fallback: a save from an empty fallback would
            // wipe the real catalogue, so failure blocks editing instead.
            .catch(() => setFetchFailed(true));
    }

    useEffect(() => {
        fetchCatalogue();
        // Mount-only; fetchCatalogue isn't a stable identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist `next` to the dev catalogue. The version bump marks every save;
    // state only advances on success, so a failed save leaves the view (and the
    // next attempt) based on what the server actually holds.
    async function saveCatalogue(next: AvatarCatalogue) {
        const bumped: AvatarCatalogue = {...next, version: next.version + 1};
        const result = await post(`${apiBindings.api}setAvatars?prod=false`, JSON.stringify(bumped), undefined);
        if (result.success) {
            setCatalogue(bumped);
        } else {
            setNotification({
                title: 'Avatar save failed',
                message: result.data?.message ?? 'The catalogue was not saved. Your change has been discarded.',
            });
        }
    }

    async function uploadElement(type: AvatarElementType, file: File) {
        if (!catalogue) return;

        let prepared: File;
        try {
            prepared = await prepareImageUpload(file);
        } catch (e) {
            setNotification({
                title: 'Could not read that image',
                message: e instanceof Error ? e.message : String(e),
            });
            return;
        }

        // Capture the source dimensions locally, like branch image uploads do.
        const dimensions = await readImageDimensions(prepared);

        const formData = new FormData();
        formData.append("file", prepared);

        const result = await postFile(`${apiBindings.api}uploadFile?id=avatar-${type}`, formData, undefined);
        if (!result.success) {
            setNotification({
                title: 'Failed to upload file.',
                message: result.data?.message,
            });
            return;
        }
        const fileName = result.data.fileName as string;

        // Soft warning only: layers share one square canvas, so a mismatched
        // source usually means the art was authored at the wrong size — but the
        // preview scales, and the mismatch may be intentional.
        const reference = AVATAR_ELEMENT_TYPES
            .flatMap(t => catalogue.elements[t])
            .find(e => e.srcWidth !== undefined && e.srcHeight !== undefined);
        if (dimensions && reference &&
            (dimensions.width !== reference.srcWidth || dimensions.height !== reference.srcHeight)) {
            setNotification({
                title: 'Dimension mismatch',
                message: `This element is ${dimensions.width}×${dimensions.height}, but existing elements are ${reference.srcWidth}×${reference.srcHeight}. Avatar layers share one canvas, so mismatched sizes may not line up in game.`,
            });
        }

        const next: AvatarCatalogue = {
            ...catalogue,
            elements: {
                ...catalogue.elements,
                [type]: [...catalogue.elements[type], {
                    id: catalogue.nextIds[type],
                    fileName: fileName,
                    srcWidth: dimensions?.width,
                    srcHeight: dimensions?.height,
                }],
            },
            // nextIds only ever grows — deleted ids are never reused.
            nextIds: {...catalogue.nextIds, [type]: catalogue.nextIds[type] + 1},
        };
        await saveCatalogue(next);
    }

    // index === palette.length appends; otherwise replaces in place.
    async function setPaletteColor(type: AvatarElementType, index: number, hex: string) {
        if (!catalogue) return;
        const palette = [...catalogue.palettes[type]];
        palette[index] = hex;
        await saveCatalogue({...catalogue, palettes: {...catalogue.palettes, [type]: palette}});
    }

    async function removePaletteColor(type: AvatarElementType, index: number) {
        if (!catalogue) return;
        const palette = catalogue.palettes[type].filter((_, i) => i !== index);
        await saveCatalogue({...catalogue, palettes: {...catalogue.palettes, [type]: palette}});
    }

    async function deleteElement(type: AvatarElementType, element: AvatarElement) {
        if (!(await confirmDelete(`${AVATAR_TYPE_LABELS[type]} #${element.id}`, 'avatar element'))) return;
        if (!catalogue) return;
        const next: AvatarCatalogue = {
            ...catalogue,
            elements: {
                ...catalogue.elements,
                [type]: catalogue.elements[type].filter(e => e.id !== element.id),
            },
        };
        await saveCatalogue(next);
    }

    // Push the current catalogue to the live service. Confirmed because it
    // impacts live players immediately (and copies element images to prod).
    async function publishLive() {
        if (!catalogue) return;
        const proceed = await confirm({
            title: 'Publish avatars to LIVE SERVICE',
            body: <p>{`Publish avatar catalogue v${catalogue.version} to the LIVE SERVICE? This immediately impacts live players and copies the element images to live storage.`}</p>,
            confirmText: 'Publish to LIVE SERVICE',
            danger: true,
        });
        if (!proceed) return;
        setLoading(true);
        const result = await post(`${apiBindings.api}setAvatars?prod=true`, JSON.stringify(catalogue), undefined);
        setLoading(false);
        if (result.success) {
            setNotification({
                title: 'Avatars published',
                message: 'The live service now serves this avatar catalogue.',
            });
        } else {
            setNotification({
                title: 'Publish failed',
                message: result.data?.message,
            });
        }
    }

    // The pane is always mounted (editor.view renders every pane and toggles
    // display), so the gate lives here as well as on the sidebar button.
    if (!canEditAvatars) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, gap: 12, padding: 40, textAlign: 'center'}}>
                <p className={'text header'}>Avatars</p>
                <p className={'muted-text'}>The avatar catalogue is global and saves immediately, so editing it needs Tester access.</p>
            </div>
        );
    }

    if (fetchFailed) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, gap: 12, padding: 40, textAlign: 'center'}}>
                <p className={'text header'}>Couldn't load the avatar catalogue</p>
                <p className={'muted-text'} style={{maxWidth: 460}}>
                    Editing is blocked until the catalogue loads — saving over a catalogue we never saw would wipe it.
                </p>
                <button onClick={fetchCatalogue}>Retry</button>
            </div>
        );
    }

    if (!catalogue) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, padding: 40}}>
                <p className={'muted-text'}>Loading avatars…</p>
            </div>
        );
    }

    return (
        <div className={'layout vertical box flex-grow'} style={{gap: 20, padding: 16}}>
            <div className={'layout horizontal center stretch gap'}>
                <div className={'layout vertical'} style={{flexGrow: 1}}>
                    <p className={'text header'}>Avatars</p>
                    <p className={'muted-text'} style={{fontSize: 13}}>
                        Global (not per-branch) — every change saves immediately to the dev catalogue. Catalogue v{catalogue.version}.
                    </p>
                </div>
                {canPublishAvatarsLive &&
                    <button className={'export-button production'} onClick={publishLive}>Publish to Live</button>}
            </div>
            <AvatarComposite catalogue={catalogue}/>
            {AVATAR_ELEMENT_TYPES.map(type =>
                <AvatarSection key={type}
                               type={type}
                               elements={catalogue.elements[type]}
                               palette={catalogue.palettes[type]}
                               tint={tints[type]}
                               onTintChange={value => setTints({...tints, [type]: value})}
                               onPaletteChange={(index, color) => setPaletteColor(type, index, color)}
                               onPaletteRemove={index => removePaletteColor(type, index)}
                               onUpload={file => uploadElement(type, file)}
                               onDelete={element => deleteElement(type, element)}/>
            )}
        </div>
    );
}

function AvatarSection(props: {
    type: AvatarElementType;
    elements: AvatarElement[];
    palette: string[];
    tint: RiftColor;
    onTintChange(value: RiftColor): void;
    onPaletteChange(index: number, color: string): void;
    onPaletteRemove(index: number): void;
    onUpload(file: File): Promise<void>;
    onDelete(element: AvatarElement): void;
}) {
    const css = riftToCss(props.tint);
    return (
        <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
            <div className={'layout horizontal center stretch gap'}>
                <p className={'text header'} style={{flexGrow: 1}}>
                    {AVATAR_TYPE_LABELS[props.type]}
                    {OPTIONAL_AVATAR_TYPES.includes(props.type) &&
                        <span className={'muted-text'} style={{fontWeight: 'normal', fontSize: 13}}> (optional)</span>}
                </p>
                {/* Preview tint for this section's tiles — display only. */}
                <ColorControl value={props.tint} onChange={props.onTintChange}/>
                <SectionUploadButton onFile={props.onUpload}/>
            </div>
            {/* The saved palette: what the game offers players for this type.
                Empty = unconstrained (the client rolls any RGB). */}
            <div className={'layout horizontal center gap'} style={{flexWrap: 'wrap', gap: 8}}>
                <span className={'muted-text'} style={{fontSize: 13}}>Palette:</span>
                {props.palette.map((hex, index) =>
                    <div key={index} className={'layout horizontal center'} style={{gap: 2}}>
                        <ColorControl value={hexToRift(hex)}
                                      onChange={value => props.onPaletteChange(index, riftToHex(value))}/>
                        <button title={'Remove color'}
                                style={{padding: '2px 5px'}}
                                onClick={() => props.onPaletteRemove(index)}>
                            <span className={'fa-solid fa-xmark'} style={{fontSize: 11}}/>
                        </button>
                    </div>
                )}
                <button title={'Add a color'}
                        onClick={() => props.onPaletteChange(props.palette.length, '808080')}>
                    <span className={'fa-solid fa-plus'}/> Color
                </button>
                {props.palette.length === 0 &&
                    <span className={'muted-text'} style={{fontSize: 12}}>
                        empty — the game picks any random color
                    </span>}
            </div>
            <div className={'layout horizontal gap'} style={{flexWrap: 'wrap', gap: 12}}>
                {props.elements.map(element =>
                    <div key={element.id} className={'layout vertical center'} style={{gap: 4}}>
                        {/* Dark backing so a light tint still reads. */}
                        <div style={{position: 'relative', width: 96, height: 96, backgroundColor: '#21262d', borderRadius: 6}}>
                            <TintedPreview fileName={element.fileName} color={css} size={96}/>
                            <span style={{position: 'absolute', top: 2, left: 6, fontSize: 11, color: 'var(--muted-text-color)'}}>
                                #{element.id}
                            </span>
                        </div>
                        <p className={'muted-text'} style={{fontSize: 12}}>
                            {element.srcWidth !== undefined && element.srcHeight !== undefined
                                ? `${element.srcWidth}×${element.srcHeight}`
                                : 'unknown size'}
                        </p>
                        <button title={'Delete element'} onClick={() => props.onDelete(element)}>
                            <span className={'fa-solid fa-trash'}/>
                        </button>
                    </div>
                )}
                {props.elements.length === 0 &&
                    <p className={'muted-text'}>No elements yet.</p>}
            </div>
        </div>
    );
}

// Per-section upload button. Same ReactImageUploading pattern as the branch
// image UploadButton, but deliberately NOT that component (nor app.context's
// uploadFile) — those write branch images into the RemoteConstants state,
// while this hands the picked file to the catalogue flow.
function SectionUploadButton(props: {onFile(file: File): Promise<void>}) {
    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState<ImageListType>([]);

    async function onChange(value: ImageListType) {
        if (loading) return;
        setImages(value as never[]);

        const file = value[0]?.file;
        if (!file) return;

        setLoading(true);
        await props.onFile(file);
        setImages([]);
        setLoading(false);
    }

    return (
        <ReactImageUploading value={images} onChange={onChange} maxNumber={1}>
            {({onImageUpload}) => (
                <button disabled={loading} title={'Upload a new element'} onClick={onImageUpload}>
                    <span className={loading ? 'fa-solid fa-spinner' : 'fa-solid fa-plus'}/> Add
                </button>
            )}
        </ReactImageUploading>
    );
}

export default AvatarEditorView;
